/**
 * Forgot Password Page - Canvas Sage design
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { HeroImage } from '@/components/brand';
import { T, BRAND, BRAND_IMAGES } from '@/styles/tokens';

// Staggered fade-in animation hook
const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
    transition: prefersReducedMotion ? 'none' : 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async () => {
    setError('');
    setIsLoading(true);
    
    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      console.error('Reset password error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Try again later.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Check your connection.');
      } else {
        setError('Failed to send reset email. Try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const canSubmit = email && !isLoading;

  // Animation timing
  const heroStyle = useFadeIn(0);
  const navStyle = useFadeIn(150);
  const headingStyle = useFadeIn(300);
  const subtitleStyle = useFadeIn(400);
  const contentStyle = useFadeIn(500);
  const buttonStyle = useFadeIn(600);

  // Inline styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100dvh',
    background: BRAND.cream,
    display: 'flex',
    flexDirection: 'column',
  };

  const navBarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    position: 'relative',
  };

  const backButtonStyle: React.CSSProperties = {
    width: 44,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background 0.2s',
  };

  const wordmarkStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    pointerEvents: 'none',
  };

  const contentWrapperStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 24px',
    paddingTop: 40,
    paddingBottom: 40,
  };

  const maxWidthStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 380,
  };

  const headingTextStyle: React.CSSProperties = {
    fontFamily: BRAND.fontSerif,
    fontSize: 26,
    fontWeight: 700,
    color: BRAND.ink,
    textAlign: 'center',
    marginBottom: 8,
  };

  const subtitleTextStyle: React.CSSProperties = {
    fontFamily: BRAND.fontSans,
    fontSize: 14,
    color: BRAND.inkLight,
    textAlign: 'center',
    marginBottom: 32,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 50,
    padding: '0 16px',
    fontFamily: BRAND.fontSans,
    fontSize: 16,
    color: BRAND.ink,
    background: BRAND.cream,
    border: `2px solid ${BRAND.inkFaint}`,
    borderRadius: 8,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  const submitButtonStyle: React.CSSProperties = {
    width: '100%',
    height: 50,
    fontFamily: BRAND.fontSans,
    fontSize: 16,
    fontWeight: 700,
    color: T.white,
    background: canSubmit ? `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.tealDark})` : T.border,
    border: 'none',
    borderRadius: 8,
    cursor: canSubmit ? 'pointer' : 'not-allowed',
    opacity: canSubmit ? 1 : 0.5,
    boxShadow: canSubmit ? '0 4px 16px rgba(61,171,165,0.2)' : 'none',
    transition: 'all 0.2s',
    marginTop: 24,
  };

  const linkTextStyle: React.CSSProperties = {
    fontFamily: BRAND.fontSans,
    fontSize: 14,
    color: BRAND.inkLight,
    textAlign: 'center',
    marginTop: 20,
  };

  const successContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 60,
  };

  const checkmarkCircleStyle: React.CSSProperties = {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: BRAND.tealLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  };

  const successHeadingStyle: React.CSSProperties = {
    fontFamily: BRAND.fontSerif,
    fontSize: 24,
    fontWeight: 700,
    color: BRAND.ink,
    textAlign: 'center',
    marginBottom: 12,
  };

  const successTextStyle: React.CSSProperties = {
    fontFamily: BRAND.fontSans,
    fontSize: 14,
    color: BRAND.inkLight,
    textAlign: 'center',
    lineHeight: 1.5,
  };

  const errorBoxStyle: React.CSSProperties = {
    padding: 12,
    background: '#FEE2E2',
    color: T.danger,
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #FECACA',
    marginBottom: 20,
    fontFamily: BRAND.fontSans,
  };

  return (
    <div style={containerStyle}>
      {/* Hero Image */}
      <div style={heroStyle}>
        <HeroImage
          src={BRAND_IMAGES.hero}
          height={180}
          opacity={0.15}
          objectPosition="center 40%"
          fadeColor={BRAND.cream}
        />
      </div>

      {/* Inline Navigation */}
      <nav style={{ ...navBarStyle, ...navStyle }}>
        <button
          onClick={() => navigate(-1)}
          style={backButtonStyle}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.05)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND.ink} strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div style={wordmarkStyle}>
          <span style={{ fontFamily: BRAND.fontSerif, fontSize: 18, fontWeight: 700, color: BRAND.teal }}>
            True
          </span>
          <span style={{ fontFamily: BRAND.fontSerif, fontSize: 18, fontWeight: 700, color: BRAND.coral }}>
            Price
          </span>
        </div>

        <div style={{ width: 44 }} />
      </nav>

      {/* Content */}
      <main style={contentWrapperStyle}>
        <div style={maxWidthStyle}>
          {success ? (
            <div style={{ ...successContainerStyle, ...contentStyle }}>
              <div style={checkmarkCircleStyle}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={BRAND.teal} strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 style={successHeadingStyle}>Check your email</h2>
              <p style={successTextStyle}>
                We sent a password reset link to <strong>{email}</strong>
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{ ...submitButtonStyle, marginTop: 32 }}
              >
                Back to Sign In
              </button>
            </div>
          ) : (
            <>
              <h1 style={{ ...headingTextStyle, ...headingStyle }}>Reset your password</h1>
              <p style={{ ...subtitleTextStyle, ...subtitleStyle }}>
                Enter your email to receive a reset link
              </p>

              <div style={contentStyle}>
                {error && <div style={errorBoxStyle}>{error}</div>}

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  style={inputStyle}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = BRAND.teal;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND.tealLight}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = BRAND.inkFaint;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />

                <button onClick={handleSubmit} disabled={!canSubmit} style={submitButtonStyle}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>

              <div style={{ ...buttonStyle, ...linkTextStyle }}>
                Remember your password?{' '}
                <Link
                  to="/login"
                  style={{ color: BRAND.teal, fontWeight: 600, textDecoration: 'none' }}
                >
                  Sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default ForgotPasswordPage;
